import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import i18next from 'i18next';

import { Button } from 'components/Button';

import {
  packageContext as packageContextTypes,
} from 'lib/CustomPropTypes';
import { isBlogLive } from 'lib/liveBlog';
import { fetchLatestCardsForPkg } from 'redux/modules/liveBlog';
import { getLiveBlogArticleId, querySettings } from 'lib/liveBlogPkgHelpers';
import { arraysAreEqual } from 'lib/arrayUtils';
import { postWebViewMessage } from 'lib/postWebViewMessage';
import { getOrderedLiveBlogCards } from 'lib/getOrderedLiveBlogCards';
import { PACKAGE_TYPES } from 'lib/constants';

import CardList from './CardList';
import './styles.themed.scss';

const {
  LIVE_BLOG_POLLING_INTERVAL = 30000,
} = process.env;

/**
 * Maps Redux state to component props
 * @param {object} root0 - Redux state
 * @param {object} root0.liveBlog - Live blog state
 * @returns {object} Mapped props
 */
const mapStateToProps = ({
  liveBlog,
}) => ({
  liveBlogPkgInstances: liveBlog.pkgInstances,
});

/**
 * LiveBlog component for displaying live blog content
 * @param {object} props - Component props
 * @param {Function} props.dispatch - Redux dispatch function
 * @param {boolean} [props.isEmbedded=false] - Whether component is embedded
 * @param {boolean} [props.isWebEmbed=false] - Whether component is web embedded
 * @param {boolean} [props.hasPolling=true] - Whether to enable polling
 * @param {boolean} [props.showLiveBadge=false] - Whether to show live badge
 * @param {object} [props.content={}] - Package content
 * @param {number} [props.itemIndex=0] - Index of live blog content item
 * @param {object} [props.liveBlogPkgInstances={}] - Live blog package instances
 * @param {boolean} [props.isMultiStoryline=false] - Whether multi-storyline
 * @param {'reportedStyle' | 'chatStyle' | 'storylineChatStyle'} [props.liveBlogStyle='reportedStyle'] - Live blog style
 * @param {boolean} [props.renderCardListByline=false] - Whether to render card list byline
 * @returns {JSX.Element|null} Rendered component
 */
const LiveBlogComponent = ({
  dispatch,
  isEmbedded = false,
  isWebEmbed = false,
  hasPolling = true,
  showLiveBadge = false,
  content = {},
  itemIndex = 0,
  liveBlogPkgInstances = {},
  isMultiStoryline = false,
  liveBlogStyle = 'reportedStyle',
  renderCardListByline = false,
}) => {
  const context = useContext(packageContextTypes);
  const { railContext = null, isRailAdjacent = false } = context || {};
  const [renderedCardIds, setRenderedCardIds] = useState([]);
  const cardPollIntervalRef = useRef(null);

  const articleId = useMemo(
    () => getLiveBlogArticleId(content, itemIndex),
    [content, itemIndex],
  );

  /**
   * Queries for latest cards
   */
  const queryCards = useCallback(() => {
    const { page, queryLimit, shownOnFronts } = querySettings;
    const fetchingLatest = liveBlogPkgInstances?.[articleId]?.fetchingLatest ?? false;

    if (!fetchingLatest) {
      dispatch(fetchLatestCardsForPkg({
        articleId,
        queryLimit,
        page,
        shownOnFronts,
      }));
    }
  }, [dispatch, articleId, liveBlogPkgInstances]);

  /**
   * Retrieves the cards to render based on the content, item index, and live blog package instances
   * @returns {Array} An array of cards to render
   */
  const getCardsToRender = useCallback(() => getOrderedLiveBlogCards({
    liveBlogPkgInstances,
    content,
    itemIndex,
    articleId,
  }), [liveBlogPkgInstances, content, itemIndex, articleId]);

  /**
   * Renders the button component
   * @param {string} itemUrl - URL for the item
   * @returns {JSX.Element} Button component
   */
  const renderButton = useCallback((itemUrl) => {
    const packageMetadata = (content?.metadata) || {};
    const {
      seeAllText,
      seeAllUrl,
    } = packageMetadata;

    return (
      <div className="live-blog__button-wrapper">
        <Button
          size="small"
          type="link"
          url={seeAllUrl || itemUrl}
          additionalClasses="live-blog__button"
          title={seeAllText || i18next.t('See All')}
        />
      </div>
    );
  }, [content]);

  useEffect(() => {
    if (hasPolling) {
      if (cardPollIntervalRef.current) {
        clearInterval(cardPollIntervalRef.current);
      }

      queryCards();
      cardPollIntervalRef.current = setInterval(queryCards, LIVE_BLOG_POLLING_INTERVAL);
    }

    return () => {
      if (cardPollIntervalRef.current) {
        clearInterval(cardPollIntervalRef.current);
        cardPollIntervalRef.current = null;
      }
    };
  }, [hasPolling, articleId]);

  useEffect(() => {
    if (isEmbedded) {
      const cardsToRender = getCardsToRender();
      const newCardIds = cardsToRender.map(({ id }) => id);
      const cardsHaveChanged = !arraysAreEqual(newCardIds, renderedCardIds);
      if (cardsHaveChanged) {
        setRenderedCardIds(newCardIds);
        postWebViewMessage('LIVE_BLOG_UPDATE');
      }
    }
  }, [isEmbedded, liveBlogPkgInstances, content, itemIndex, articleId]);

  const item = (content?.items?.[itemIndex]) || {};
  const { id: itemId, type: itemType, metadata } = item;
  const validTypes = ['liveBlog', 'article'];
  if (!validTypes.includes(itemType)) return null;

  const cardsToRender = getCardsToRender();

  const packageId = content?.id;
  const packageType = content?.type;

  const isLiveBlogPackage = packageType === PACKAGE_TYPES.POST_LIST;

  const { item: { cards } } = item;
  const mostRecentPublishedAt = Array.isArray(cards) && cards.length > 0
    ? cards.reduce((latest, current) => {
      const currentPublishedAt = current?.date?.publishedAt;
      if (!latest || currentPublishedAt > latest) {
        return currentPublishedAt;
      }
      return latest;
    }, null) : null;
  const {
    title,
    description,
    hideLiveBlogBadge = false,
    hideLiveBlogHeadlineText = false,
  } = metadata || {};

  const hasHeader = title || description;
  const itemUrl = item?.computedValues?.url;
  const isLiveCurrently = isBlogLive(mostRecentPublishedAt) || (packageType === PACKAGE_TYPES.STORYLINE && !hideLiveBlogBadge);
  const isInRail = railContext !== null;

  let liveBlogItemsToDisplay = 4;
  if (packageType === PACKAGE_TYPES.STORYLINE
    || isMultiStoryline
    || (content.type === PACKAGE_TYPES.MULTI_STORYLINE3 && isEmbedded)) {
    liveBlogItemsToDisplay = Number(metadata.maxBlogItems ?? 0);
  }

  const isChatStyle = liveBlogStyle === 'chatStyle';
  const isStorylineChatStyle = liveBlogStyle === 'storylineChatStyle';

  return (
    <div
      className={classNames('live-blog', `${liveBlogStyle}`, {
        'pkg live-blog--standalone': isLiveBlogPackage,
        [`live-blog--${packageType}`]: !isLiveBlogPackage,
        'is-live': isLiveCurrently,
        'has-headline': !hideLiveBlogHeadlineText && !isEmbedded,
        'live-blog--in-rail': isInRail,
        [`cards-to-display-${liveBlogItemsToDisplay}`]: true,
      })}
      data-test="live-blog"
      data-testid="live-blog-package"
      data-packageid={packageId}
    >
      {hasHeader && <div className="live-blog__top-decoration" />}
      <div className="live-blog__content-wrapper">
        <div className="live-blog__content">
          <CardList
            id={itemId}
            content={item}
            cards={cardsToRender}
            hideHeadlineText={hideLiveBlogHeadlineText}
            isEmbedded={isEmbedded}
            isWebEmbed={isWebEmbed}
            defaultBreakingStyle={!isLiveBlogPackage}
            defaultLabel={(isLiveCurrently || isChatStyle || isStorylineChatStyle) ? 'Live Updates' : ''}
            isRailAdjacent={isRailAdjacent}
            showLiveBadge={showLiveBadge}
            isStorylinePackage={packageType === PACKAGE_TYPES.STORYLINE}
            isMultiStoryline={isMultiStoryline}
            liveBlogStyle={liveBlogStyle}
            maxCardsOverride={liveBlogItemsToDisplay}
            renderByline={renderCardListByline}
          />
        </div>
        {
          isLiveBlogPackage && renderButton(itemUrl)
        }
      </div>
    </div>
  );
};

export const LiveBlog = connect(mapStateToProps)(LiveBlogComponent);
